// client/src/components/world/Navigator.jsx
// Итерация 6.1 + 8.3d: переключатель контекста навигации.
//
// Контексты:
//   - structure           — станция/корабль/верфь/spaceport С реальными данными
//   - structure_loading   — данные ещё грузятся
//   - structure_no_data   — структура есть, но схемы нет → «Схема недоступна»
//   - surface             — планета/спутник/астероид/поверхность
//   - system              — межпланетный перелёт
//   - unknown             — неизвестный тип
//
// ⚠️ Итерация 8.3d: useStructureInfo вынесен в GameClient (Вариант A).
//    Navigator получает structureInfo через prop.

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Explore as UnknownIcon,
  Layers as LayersIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

import StructureNavigator from './StructureNavigator';
import SurfaceMinimap from './SurfaceMinimap';
import SystemMinimap from './SystemMinimap';
import { isStructureType, isSurfaceType, isSystemType } from '../../utils/structureTypes';

// ============================================================================
// ОПРЕДЕЛЕНИЕ КОНТЕКСТА
// ============================================================================

function resolveContext(characterMeta, locationInfo, structureInfo, loading) {
  const type = String(locationInfo?.type || '').toLowerCase();

  // Проверка структуры: есть ли реальные палубы или модули?
  const decks = structureInfo?.available_decks;
  const modules = structureInfo?.installed_modules;
  const hasRealDecks = Array.isArray(decks) && decks.length > 1;
  const hasModules = Array.isArray(modules) && modules.length > 0;
  const hasStructure = hasRealDecks || hasModules;

  // 1. Персонаж В СТРУКТУРЕ — проверяем данные
  if (characterMeta?.in_structure) {
    if (hasStructure) return 'structure';
    if (loading) return 'structure_loading';
    // Данных нет — «схема недоступна», даже если in_structure
    if (characterMeta?.surface) return 'surface';
    return 'structure_no_data';
  }

  // 2. На поверхности
  if (characterMeta?.surface) return 'surface';

  // 3. В перелёте
  if (characterMeta?.in_transit) return 'system';

  // 4. Fallback по типу локации
  if (isStructureType(type)) {
    if (hasStructure) return 'structure';
    if (loading) return 'structure_loading';
    return 'structure_no_data';
  }
  if (isSurfaceType(type)) return 'surface';
  if (isSystemType(type)) return 'system';

  return 'unknown';
}

// ============================================================================
// ГЛАВНЫЙ КОМПОНЕНТ
// ============================================================================

export default function Navigator({
  characterMeta,
  locationInfo,
  structureInfo = null,
  onDeckSelect,
}) {
  // 🔥 8.3d: структура приходит через prop (не грузим здесь)
  const shouldLoadStructure = isStructureType(locationInfo?.type) && !!locationInfo?.id;
  const loading = shouldLoadStructure && structureInfo === null;

  const context = useMemo(
    () => resolveContext(characterMeta, locationInfo, structureInfo, loading),
    [characterMeta, locationInfo, structureInfo, loading]
  );

  const currentDeckId = characterMeta?.deck_id || null;

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          fontSize: '0.55rem',
          display: 'block',
          mb: 0.5,
        }}
      >
        🧭 Навигация
      </Typography>

      {/* === СТРУКТУРА С ДАННЫМИ === */}
      {context === 'structure' && (
        <StructureNavigator
          locationInfo={locationInfo}
          structureInfo={structureInfo}
          loading={false}
          error={null}
          currentDeckId={currentDeckId}
          onDeckSelect={onDeckSelect}
        />
      )}

      {/* === ЗАГРУЗКА === */}
      {context === 'structure_loading' && (
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <LayersIcon
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%,100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>
            Загрузка палуб...
          </Typography>
        </Box>
      )}

      {/* === СТРУКТУРА БЕЗ СХЕМЫ === */}
      {context === 'structure_no_data' && (
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <InfoIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.55rem',
                color: 'text.secondary',
                fontWeight: 600,
              }}
            >
              Схема структуры недоступна
            </Typography>
          </Box>
          {locationInfo?.name && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.5rem',
                color: 'rgba(255,255,255,0.4)',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {locationInfo.name}
            </Typography>
          )}
        </Box>
      )}

      {/* === ПОВЕРХНОСТЬ === */}
      {context === 'surface' && (
        <SurfaceMinimap locationInfo={locationInfo} characterMeta={characterMeta} />
      )}

      {/* === СИСТЕМА === */}
      {context === 'system' && (
        <SystemMinimap locationInfo={locationInfo} characterMeta={characterMeta} />
      )}

      {/* === НЕИЗВЕСТНО === */}
      {context === 'unknown' && (
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.05)',
            textAlign: 'center',
          }}
        >
          <UnknownIcon sx={{ fontSize: 20, color: 'text.secondary', opacity: 0.3, mb: 0.25 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.55rem', display: 'block' }}
          >
            Навигация недоступна
          </Typography>
          {locationInfo?.type && (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.45rem', color: 'rgba(255,255,255,0.3)', display: 'block', mt: 0.25 }}
            >
              тип: {locationInfo.type}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
